import React, { Component } from 'react';

// Normalises catalogue plan names to the labels used by Property.Plans,
// for example the Chargify product name "Pro Plus" becomes "Pro+".
function normalizePlanName(name) {
    if (!name) {
        return name;
    }
    var trimmed = String(name).trim();
    return trimmed.toLowerCase() === 'pro plus' ? 'Pro+' : trimmed;
}

// The lowest plan that covers every selected property, and the rank of
// the plans held by the customer's keys, both derived from the plans
// catalogue (api/data/plans). A property with no Cloud V5 plan labels
// needs Bespoke.
export function computeUpgrade(plans, selectedProperties, productKeys) {
    if (!plans || plans.length === 0 || selectedProperties.length === 0) {
        return null;
    }

    var rankByName = {};
    var maxRank = 1;
    plans.forEach(function (p) {
        rankByName[normalizePlanName(p.Name)] = p.Rank;
        maxRank = Math.max(maxRank, p.Rank);
    });

    // Each property is covered from its cheapest plan upwards, so the
    // selection as a whole needs the most expensive of those cheapest
    // plans.
    var requiredRank = 1;
    selectedProperties.forEach(function (property) {
        var propPlans = property.Plans || [];
        var cheapest = maxRank + 1;
        propPlans.forEach(function (name) {
            var rank = rankByName[normalizePlanName(name)];
            if (rank !== undefined && rank < cheapest) {
                cheapest = rank;
            }
        });
        if (propPlans.length === 0) {
            cheapest = maxRank;
        }
        if (cheapest > requiredRank) {
            requiredRank = cheapest;
        }
    });

    // The customer's best held plan. ProductInfo usually returns the plan
    // display name (e.g. "Essentials"), but when the Chargify subscription
    // lookup falls back it returns the FiftyOne.MetaData product name (e.g.
    // "CloudV5Basic"), so match on both the display name and each plan's
    // ProductNames.
    var rankByProductName = {};
    plans.forEach(function (p) {
        (p.ProductNames || []).forEach(function (pn) {
            rankByProductName[pn] = p.Rank;
        });
    });

    var heldRank = 0;
    productKeys.forEach(function (key) {
        var rank = rankByName[normalizePlanName(key.product)];
        if (rank === undefined) {
            // The fallback name can be a comma-joined list of product
            // names, so take the best rank any of them maps to.
            (key.product || '').split(',').forEach(function (part) {
                var r = rankByProductName[part.trim()];
                if (r !== undefined && (rank === undefined || r > rank)) {
                    rank = r;
                }
            });
        }
        if (rank !== undefined && rank > heldRank) {
            heldRank = rank;
        }
    });

    var required = plans.find(function (p) { return p.Rank === requiredRank; });
    return {
        required: required,
        heldRank: heldRank,
        upgradeNeeded: heldRank > 0 && heldRank < requiredRank
    };
}

// Plan-mismatch upgrade prompt (issue #71). Shown once License Keys are
// present when the selected properties are not all within the
// customer's plan, naming the lowest plan that covers the whole
// selection with a link to the pricing page. Applies to every customer,
// not only Free.
export class UpgradePrompt extends Component {
    displayName = UpgradePrompt.name;

    render() {
        var upgrade = computeUpgrade(
            this.props.plans,
            this.props.selectedProperties,
            this.props.productKeys);

        if (!upgrade || !upgrade.upgradeNeeded || !upgrade.required) {
            return null;
        }

        return (
            <article className="c-card c-card--highlight">
                <header className="c-card__header">
                    <h3 className="c-card__heading">
                        Your plan does not cover all the selected properties
                    </h3>
                </header>
                <p>
                    Upgrade to <strong>{upgrade.required.Name}</strong> to
                    use everything you have selected, or remove the
                    properties outside your plan. See the
                    {' '}<a href={upgrade.required.SignUpUrl && upgrade.required.SignUpUrl.indexOf('http') === 0 ?
                        upgrade.required.SignUpUrl : 'https://51degrees.com/pricing'}
                        target="_blank" rel="noopener noreferrer"
                        className="b-link">pricing page</a> to upgrade.
                </p>
            </article>
        );
    }
}
